import { type AriaAttributes, Ref, forwardRef } from "react";
import * as Core from "@blueprintjs/core";
import classnames from "classnames";
import { useAutomation } from "~/hooks";
import { fun } from "../fun/funContext";
import { Icon, IconName, IconProps } from "./icon";
import { IllustrationIcon, type IllustrationIconProps } from "./illustrationIcon";
import { Spinner } from "./spinner";
import { type ToneProps, toneClass } from "./tone";

export interface NonIdealStateProps extends Omit<Core.NonIdealStateProps, "icon" | "iconSize" | "intent">, ToneProps, AriaAttributes {
  icon?: IconName | React.ReactElement<IconProps>;
  /** @default 50 */
  iconSize?: number;
  iconAlignment?: "top" | "left";
  loading?: boolean;
}

// Overridden native component because BlueprintJS added custom styles
// to change the stroke and colors of nested icons in the visual area
export const NonIdealState = fun(
  "nonIdealState",
  forwardRef((props: NonIdealStateProps, ref: Ref<HTMLDivElement>) => {
    const { action, children, iconAlignment, loading, tone, iconSize = 50, className, layout = "vertical", description, title } = props;

    const icon = props.icon ?? (loading ? <Spinner size={iconSize} tone="primary" /> : undefined);

    const leftIconAlignment = iconAlignment === "left";
    const ariaLabel = props["aria-label"] ?? (typeof title === "string" ? title : undefined) ?? (typeof description === "string" ? description : undefined);
    const { id } = useAutomation({ label: ariaLabel }, "presentation");

    return (
      <div
        ref={ref}
        aria-label={ariaLabel}
        className={classnames(Core.Classes.NON_IDEAL_STATE, `${Core.Classes.NON_IDEAL_STATE}-${layout}`, toneClass(tone), className, {
          "left-icon": leftIconAlignment,
        })}
        id={id}
        role="presentation"
      >
        {icon && <Icon aria-hidden icon={icon} size={iconSize} />}
        {(title || description) && (
          <div className={Core.Classes.NON_IDEAL_STATE_TEXT}>
            {title && <Core.H4>{title}</Core.H4>}
            {description && typeof description === "string" && <div>{description}</div>}
            {description && typeof description !== "string" && description}
          </div>
        )}
        {action}
        {children}
      </div>
    );
  })
);

export function toNativeProps(props: NonIdealStateProps): Core.NonIdealStateProps {
  const { icon, ...restProps } = props;

  const nativeIcon: Core.MaybeElement | undefined = icon ? <Icon icon={icon} size={Core.IconSize.LARGE * 3} /> : undefined;

  return {
    ...restProps,
    icon: nativeIcon,
  };
}

export interface NonIdealIconProps extends Omit<NonIdealStateProps, "icon" | "iconSize">, IllustrationIconProps {}

export const NonIdealIcon = (props: NonIdealIconProps) => {
  const { icon, size, tone, onClick, ...restProps } = props;

  const content = <IllustrationIcon icon={icon} size={size} tone={tone} onClick={onClick} />;

  return <NonIdealState {...toNativeProps(restProps)} icon={content} />;
};
